import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    createContext,
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import * as Icons from "@nova/component-icons"
import { ButtonPrimary, ButtonSpecial, ButtonTertiary, IconButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import classNames from "classnames"
import { get, set } from "nodash"
import { resetAircraftFilters, selectRightPanelOpen } from "./slice"
import {
    AirspaceCategory,
    AirspaceOptionPath,
    AirspaceState,
    getDefaultLocalStorageValue,
    getLocalStorageValue,
    setLocalStorageValue,
    useLocalStorageValue,
} from "tools/store/localStorageRegistry"
import { zIndex } from "@nova/constants"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import Link from "next/link"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { CategoryConfig, notams, restrictions, tfrs } from "./rightPanelCategoryConfig"
import { AdsbSection } from "./adsb/AdsbSection"
import { SpidertracksSection } from "./spidertracks/SpidertracksSection"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"

const airspaceOptions = [
    {
        icon: Icons.BarrierBlock,
        title: "Restrictions",
        panel: RestrictionsRightPanel,
        category: "restrictions",
    },
    {
        icon: Icons.ConnectingAirports,
        title: "TFRs",
        panel: TFRsPanel,
        category: "tfrs",
    },
    {
        icon: Icons.Warning,
        title: "NOTAMs",
        panel: NOTAMsPanel,
        category: "notams",
    },
] satisfies { icon: Icons.IconComponent; title: string; panel: FC; category: AirspaceCategory }[]

const PANEL_TRANSITION_MS = 280

const RightPanelStackContext = createContext<{
    push: (c: FC) => void
    pop: () => void
    flush: () => void
}>({
    push: () => undefined,
    pop: () => undefined,
    flush: () => undefined,
})
const useRightPanelStack = () => {
    return useContext(RightPanelStackContext)
}

const AnimatedPanel = ({ Panel, exiting }: { Panel: FC; exiting: boolean }) => {
    const [entered, setEntered] = useState(false)
    useEffect(() => {
        const raf = requestAnimationFrame(() => setEntered(true))
        return () => cancelAnimationFrame(raf)
    }, [])
    return (
        <div
            className={classNames("absolute inset-0 transition-transform duration-300 ease-out", {
                "translate-x-full": !entered || exiting,
                "translate-x-0": entered && !exiting,
            })}
            style={{ transitionDuration: `${PANEL_TRANSITION_MS}ms` }}
        >
            <Panel />
        </div>
    )
}

export const RightPanel = () => {
    const isRightPanelOpen = useAppSelector(selectRightPanelOpen)
    const [panelStack, setPanelStack] = useState<{ id: number; Panel: FC; exiting: boolean }[]>([])
    const nextId = useRef(0)

    const push = useCallback((Panel: FC) => {
        setPanelStack((prev) => [...prev, { id: nextId.current++, Panel, exiting: false }])
    }, [])

    const pop = useCallback(() => {
        setPanelStack((prev) => {
            if (!prev.length) return prev
            const lastId = prev[prev.length - 1].id
            setTimeout(() => {
                setPanelStack((s) => s.filter((e) => e.id !== lastId))
            }, PANEL_TRANSITION_MS)
            return prev.map((e) => (e.id === lastId ? { ...e, exiting: true } : e))
        })
    }, [])

    const flush = useCallback(() => setPanelStack([]), [])

    const contextValue = useMemo(() => ({ push, pop, flush }), [push, pop, flush])

    return (
        <div
            className="overflow-hidden flex transition-all pointer-events-auto"
            style={{
                maxWidth: isRightPanelOpen ? 240 : 0,
                zIndex: zIndex.SidePanel,
            }}
        >
            <div
                className={classNames(
                    "w-[240px] flex transition-transform relative duration-300 ease-out",
                    {
                        "translate-x-full": !isRightPanelOpen,
                        "translate-x-0": isRightPanelOpen,
                    }
                )}
            >
                <RightPanelStackContext.Provider value={contextValue}>
                    <div style={{ zIndex: 1 }}>
                        <DefaultRightPanel />
                    </div>
                    {panelStack.map(({ id, Panel, exiting }, i) => (
                        <div key={id} style={{ zIndex: i + 2 }}>
                            <AnimatedPanel Panel={Panel} exiting={exiting} />
                        </div>
                    ))}
                </RightPanelStackContext.Provider>
            </div>
        </div>
    )
}

const DefaultRightPanel = () => {
    const dispatch = useAppDispatch()
    return (
        <div className="w-[240px] bg-gray-800 flex flex-col h-full">
            <div className="h-[66px] flex items-center px-4">
                <div className="text-base telegraf">Map Options</div>
            </div>
            <div className="w-full h-px bg-gray-700" />
            <ScrollableArea className="min-h-0">
                <AdsbSection />
                <SpidertracksSection />
                <div className="w-full h-px bg-gray-700" />
                <AirspaceSection />
            </ScrollableArea>
            <div className="flex-1 min-h-[12px]" />
            <div className="p-3 w-full">
                <CustomLayerButton />
            </div>
            <div className="w-full h-px bg-gray-700" />
            <div
                className="p-3 flex justify-center items-center cursor-pointer text-gray-100 hover:text-white"
                onClick={() => {
                    dispatch(resetAircraftFilters())
                    setLocalStorageValue(
                        "airspaceFilters",
                        getDefaultLocalStorageValue("airspaceFilters")
                    )
                }}
            >
                Reset to Default
            </div>
        </div>
    )
}

const CustomLayerButton = () => {
    const confirmationModal = useConfirmationModalContext()
    return (
        <ButtonSpecial
            title="Add Custom Layer"
            className="w-full"
            onClick={() =>
                confirmationModal.confirm({
                    RenderComponent: ({ cancel }) => {
                        return (
                            <HighPriorityNovaModalContainer
                                title={"Add Custom Layers"}
                                closeModal={cancel}
                                className="w-[500px]"
                            >
                                <div className="flex justify-center items-center gap-8 py-4">
                                    <div className="flex-1 text-base text-white">
                                        Need a custom integration? Contact us for a qoute.
                                    </div>
                                    <Link href="mailto:info@mapnova.com" className="flex-1">
                                        <ButtonSpecial title="Contact Sales" className="w-full" />
                                    </Link>
                                </div>
                            </HighPriorityNovaModalContainer>
                        )
                    },
                })
            }
        />
    )
}

const OfficialSourcesWarning = () => {
    const confirmationModal = useConfirmationModalContext()
    return (
        <div className="flex flex-col gap-1 items-center p-4 text-center shrink-0">
            <Icons.SmallIcon Icon={Icons.Warning} className="text-yellow-500" />
            <small className="text-gray-200 max-w-[160px]">
                Verify with official sources before operational use.
            </small>
            <small
                className="text-blue-500 hover:underline cursor-pointer"
                onClick={() =>
                    confirmationModal.confirm({
                        RenderComponent: ({ cancel }) => {
                            return (
                                <HighPriorityNovaModalContainer
                                    title="Airspace Data Disclaimer"
                                    closeModal={cancel}
                                    bottomBar={
                                        <div className="flex justify-end w-full">
                                            <ButtonPrimary title="Close" onClick={cancel} />
                                        </div>
                                    }
                                    className="w-128"
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="text-gray-100">
                                            This data is informational and intended to assist with
                                            situational awareness.
                                        </div>
                                        <div className="text-gray-100">
                                            It should be used alongside other authoritative sources
                                            and is not to be treated as the sole reference for
                                            operations or safety-critical decisions.
                                        </div>
                                    </div>
                                </HighPriorityNovaModalContainer>
                            )
                        },
                    })
                }
            >
                Learn more
            </small>
        </div>
    )
}

const AirspaceSection = () => {
    const airspacesEnabled = useLocalStorageValue("airspaceFilters").enabled
    return (
        <div className="flex flex-col">
            <div
                className="px-4 py-[14px] flex items-center justify-between cursor-pointer hover:bg-white-100"
                onClick={() => {
                    const airspace = getLocalStorageValue("airspaceFilters")
                    setLocalStorageValue("airspaceFilters", {
                        ...airspace,
                        enabled: !airspace.enabled,
                    })
                }}
            >
                <div className="telegraf">Airspace</div>
                <ToggleSwitch isToggled={airspacesEnabled} setIsToggled={() => {}} />
            </div>
            <ExpandableContent isOpen={airspacesEnabled}>
                <div className="px-3 flex flex-col pb-2">
                    {airspaceOptions.map((props) => (
                        <AirspaceSubMenuOptions key={props.title} {...props} />
                    ))}
                </div>
            </ExpandableContent>
        </div>
    )
}

const AirspaceSubMenuOptions = ({
    icon,
    title,
    panel,
    category,
}: (typeof airspaceOptions)[number]) => {
    const { push } = useRightPanelStack()
    const isVisible = useLocalStorageValue("airspaceFilters").categoriesVisible[category]
    return (
        <div
            key={title}
            className="px-1 py-3 flex items-center gap-2 cursor-pointer hover:bg-white-100 group rounded-lg"
            onClick={() => push(panel)}
        >
            <Icons.SmallIcon Icon={icon} className="text-gray-100" />
            <div
                className={classNames("text-xs select-none", {
                    "text-gray-50 group-hover:text-white": isVisible,
                    "text-gray-400 group-hover:text-gray-200": !isVisible,
                })}
            >
                {title}
            </div>
            <div className="grow" />
            <div className="flex gap-1 items-center">
                <div
                    className="w-5 h-5 shrink-0 flex justify-center items-center group/visibility hover:bg-gray-700 cursor-pointer rounded-md"
                    onClick={(e) => {
                        e.stopPropagation()
                        const airspace = getLocalStorageValue("airspaceFilters")
                        setLocalStorageValue("airspaceFilters", {
                            ...airspace,
                            categoriesVisible: {
                                ...airspace.categoriesVisible,
                                [category]: !isVisible,
                            },
                        })
                    }}
                >
                    <Icons.ExtraSmallIcon
                        Icon={isVisible ? Icons.VisibilityEye : Icons.VisibilityEyeOff}
                        className={classNames({
                            "text-gray-300 group-hover/visibility:text-gray-100 opacity-0 group-hover:opacity-100":
                                isVisible,
                            "text-gray-500 group-hover/visibility:text-gray-300": !isVisible,
                        })}
                    />
                </div>
                <Icons.SmallIcon
                    Icon={Icons.ChevronRight}
                    className="text-gray-500 group-hover:text-blue-500"
                />
            </div>
        </div>
    )
}

function AirspaceOptionsPanel<C extends keyof AirspaceState>({
    config,
}: {
    config: CategoryConfig<C>
}) {
    const { pop } = useRightPanelStack()
    const airspace = useLocalStorageValue("airspaceFilters")
    const confirmationModal = useConfirmationModalContext()
    return (
        <div className="w-[240px] bg-gray-800 flex flex-col h-full">
            <div className="h-[88px] flex flex-col justify-center px-4 shrink-0">
                <IconButton onClick={pop} className="text-gray-300">
                    <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                </IconButton>
                <div className="flex justify-between items-center">
                    <div className="text-base telegraf select-none">{config.label}</div>
                    <Icons.ExtraSmallIcon
                        Icon={Icons.Question}
                        className="text-white-300 hover:text-white-500 cursor-pointer"
                        onClick={() =>
                            confirmationModal.confirm({
                                RenderComponent: ({ cancel }) => (
                                    <CategoryDescriptionsModal config={config} cancel={cancel} />
                                ),
                            })
                        }
                    />
                </div>
            </div>
            <div className="w-full h-px bg-gray-700 shrink-0" />
            <ScrollableArea className="min-h-0 shrink">
                <div className="px-4 flex flex-col">
                    {config.sections.map(({ label, subKey, options }, i) => {
                        const basePath = subKey ? `${config.category}.${subKey}` : config.category
                        const paths = options.map(
                            (o) => `${basePath}.${o.key}` as AirspaceOptionPath
                        )
                        const allSelected = paths.every((p) => get(airspace, p))
                        return (
                            <div
                                key={label}
                                className={classNames("flex flex-col py-3", {
                                    "border-b border-b-gray-700": i !== config.sections.length - 1,
                                })}
                            >
                                <div className="flex justify-between items-center">
                                    <small className="xs text-gray-400 select-none">{label}</small>
                                    <ButtonTertiary
                                        title={allSelected ? "Deselect all" : "Select all"}
                                        onClick={() => {
                                            const currentAirspace =
                                                getLocalStorageValue("airspaceFilters")
                                            paths.forEach((path) =>
                                                set(currentAirspace, path, !allSelected)
                                            )
                                            setLocalStorageValue("airspaceFilters", currentAirspace)
                                        }}
                                        className="[&_h5]:!text-xs [&_h5]:!min-h-0 !py-1 !px-1.5 !h-fit"
                                    />
                                </div>
                                {options.map((option) => (
                                    <AirspaceOptionsPanelOption
                                        key={option.key}
                                        path={`${basePath}.${option.key}` as AirspaceOptionPath}
                                        label={option.label}
                                    />
                                ))}
                            </div>
                        )
                    })}
                </div>
            </ScrollableArea>
            <div className="grow" />
            <OfficialSourcesWarning />
        </div>
    )
}

function CategoryDescriptionsModal<C extends keyof AirspaceState>({
    config,
    cancel,
}: {
    config: CategoryConfig<C>
    cancel: () => void
}) {
    const [activeSectionLabel, setActiveSectionLabel] = useState(config.sections[0].label)
    const section = config.sections.find((s) => s.label === activeSectionLabel)!
    return (
        <HighPriorityNovaModalContainer
            title={config.label}
            closeModal={cancel}
            className="w-[500px]"
            bottomBar={
                <div className="flex justify-end w-full">
                    <ButtonPrimary title="Close" onClick={cancel} />
                </div>
            }
            hideTitleUnderline={config.sections.length > 1}
        >
            <div className="flex flex-col gap-4">
                {config.sections.length > 1 && (
                    <div className="flex">
                        {config.sections.map((s) => (
                            <TabHeader
                                key={s.label}
                                currentMode={activeSectionLabel}
                                mode={s.label}
                                setActiveMode={() => setActiveSectionLabel(s.label)}
                                title={s.label}
                            />
                        ))}
                        <div className="flex-1 border-b-2 border-b-white-200" />
                    </div>
                )}
                <ScrollableArea className="max-h-[500px] min-h-0">
                    <div className="flex flex-col">
                        {section.options.map((option, i) => (
                            <div
                                key={option.key}
                                className={classNames("flex py-3 items-center", {
                                    "border-b border-gray-700": i !== section.options.length - 1,
                                })}
                            >
                                <div className="w-28 shrink-0 text-base text-white">
                                    {option.label}
                                </div>
                                <div className="text-gray-200 shrink min-w-0 break-words">
                                    {option.description}
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollableArea>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

function AirspaceOptionsPanelOption({ path, label }: { path: AirspaceOptionPath; label: string }) {
    const value = !!get(useLocalStorageValue("airspaceFilters"), path)
    return (
        <div
            className="flex items-center gap-3 py-1 cursor-pointer hover:bg-white-100"
            onClick={() => {
                const airspace = getLocalStorageValue("airspaceFilters")
                set(airspace, path, !value)
                setLocalStorageValue("airspaceFilters", airspace)
            }}
        >
            <TriCheckbox state={value ? "checked" : "unchecked"} />
            <div className="text-xs select-none">{label}</div>
        </div>
    )
}

function RestrictionsRightPanel() {
    return <AirspaceOptionsPanel config={restrictions} />
}

function TFRsPanel() {
    return <AirspaceOptionsPanel config={tfrs} />
}
function NOTAMsPanel() {
    return <AirspaceOptionsPanel config={notams} />
}
